import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Activity, Users } from 'lucide-react';
import { getMintActivity, formatActivityEvent, getSOLUSDValue } from '../utils/nftActivity.js';
import { getEditionHolders } from '../utils/marketplaceQueries.js';
import { getProfileUrl } from '../utils/saleHistoryUtils';
import { supabase } from '../lib/supabase';
import VerifiedBadge from './VerifiedBadge';

const PieceActivityAndHolders = ({ pieceId, pieceName, basePrice, solPrice = 100, totalQuantity = 1, groupedPieceIds = null }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('activity');
  const [saleHistory, setSaleHistory] = useState([]);
  const [holders, setHolders] = useState([]);
  const [isLoadingActivity, setIsLoadingActivity] = useState(true);
  const [isLoadingHolders, setIsLoadingHolders] = useState(false);
  const [showAll, setShowAll] = useState(false);

  // Load activity history
  useEffect(() => {
    const loadActivityHistory = async () => {
      setIsLoadingActivity(true);
      
      try {
        // Extract numeric ID from strings like "supabase_123" or just "123"
        const numericId = typeof pieceId === 'string' && pieceId.includes('supabase_')
          ? parseInt(pieceId.replace('supabase_', ''))
          : parseInt(pieceId);
        
        console.log('🔍 [ACTIVITY] Fetching piece data for ID:', pieceId, '-> numeric ID:', numericId);
        console.log('🔄 [ACTIVITY] Loading/Refreshing activity history...');
        
        if (isNaN(numericId)) {
          console.error('❌ [ACTIVITY] Invalid piece ID:', pieceId);
          setSaleHistory([]);
          return;
        }
        
        // First, fetch the piece to get its mint_address
        const { data: pieceData, error: pieceError } = await supabase
          .from('pieces')
          .select('mint_address, name, minted_at')
          .eq('id', numericId)
          .maybeSingle();

        if (pieceError) {
          console.error('❌ [ACTIVITY] Error fetching piece:', pieceError);
          setSaleHistory([]);
          return;
        }

        console.log('📦 [ACTIVITY] Piece data:', {
          id: pieceId,
          name: pieceData?.name,
          mint_address: pieceData?.mint_address?.substring(0, 20) + '...',
          minted_at: pieceData?.minted_at
        });

        if (!pieceData || !pieceData.mint_address) {
          console.log('⚠️ [ACTIVITY] Piece has no mint_address yet - not minted');
          setSaleHistory([]);
          return;
        }

        // Check if mint address looks valid
        const mintAddress = pieceData.mint_address;
        if (/^\d+$/.test(mintAddress) || mintAddress.length < 32) {
          console.warn('⚠️ [ACTIVITY] Invalid mint_address format:', mintAddress);
          setSaleHistory([]);
          return;
        }

        console.log('✅ [ACTIVITY] Valid mint_address found:', mintAddress.substring(0, 15) + '...');
        
        // Now fetch activity using the mint_address
        const activity = await getMintActivity(mintAddress);
        
        console.log(`📊 [ACTIVITY] Retrieved ${activity?.length || 0} activity records`);
        
        if (activity && activity.length > 0) {
          console.log('📝 [ACTIVITY] Sample activity:', activity[0]);
          
          const formattedActivity = activity.map((event, index) => ({
            id: event.signature || `${event.mint_address}-${event.event_type}-${event.event_timestamp}-${index}`,
            timeAgo: event.time_ago,
            exactDateTime: new Date(event.event_timestamp * 1000).toLocaleString(),
            type: event.event_type,
            buyer: event.from_wallet ? `${event.from_wallet.slice(0, 4)}...${event.from_wallet.slice(-4)}` : 'Unknown',
            buyerFull: event.from_wallet || 'Unknown',
            seller: event.to_wallet ? `${event.to_wallet.slice(0, 4)}...${event.to_wallet.slice(-4)}` : 'Unknown',
            sellerFull: event.to_wallet || 'Unknown',
            priceFormatted: event.sol_amount ? `${parseFloat(event.sol_amount).toFixed(2)} SOL` : 'N/A',
            usdPriceFormatted: event.sol_amount ? `~$${getSOLUSDValue(parseFloat(event.sol_amount), solPrice)}` : 'N/A'
          }));
          
          console.log('✅ [ACTIVITY] Formatted activity:', formattedActivity);
          setSaleHistory(formattedActivity);
        } else {
          console.log('ℹ️ [ACTIVITY] No activity found - piece has not been traded yet');
          setSaleHistory([]);
        }
      } catch (error) {
        console.error('❌ [ACTIVITY] Error loading activity history:', error);
        setSaleHistory([]);
      } finally {
        setIsLoadingActivity(false);
      }
    };

    if (pieceId) {
      loadActivityHistory();
    } else {
      setIsLoadingActivity(false);
      setSaleHistory([]);
    }
  }, [pieceId, solPrice]);

  // Set up real-time activity refresh (automatically refresh when new activities are added)
  useEffect(() => {
    if (!pieceId) return;

    console.log('📡 [ACTIVITY] Setting up real-time activity updates for piece:', pieceId);
    
    // Subscribe to nft_activity table changes
    const activitySubscription = supabase
      .channel(`piece_activity_${pieceId}`)
      .on('postgres_changes', 
        { 
          event: 'INSERT', 
          schema: 'public', 
          table: 'nft_activity'
        }, 
        (payload) => {
          console.log('🔔 [ACTIVITY] New activity detected!', payload.new);
          console.log('🔄 [ACTIVITY] Refreshing activity list...');
          
          // Re-fetch activities to show the new one
          const numericId = typeof pieceId === 'string' && pieceId.includes('supabase_')
            ? parseInt(pieceId.replace('supabase_', ''))
            : parseInt(pieceId);
          
          // Simple manual refresh after 500ms delay
          setTimeout(async () => {
            setIsLoadingActivity(true);
            try {
              const { data: pieceData } = await supabase
                .from('pieces')
                .select('mint_address, name, minted_at')
                .eq('id', numericId)
                .maybeSingle();

              if (pieceData?.mint_address) {
                const activity = await getMintActivity(pieceData.mint_address);
                if (activity && activity.length > 0) {
                  const formattedActivity = activity.map(event => ({
                    id: event.signature || `${event.mint_address}-${event.event_type}-${event.event_timestamp}`,
                    timeAgo: event.time_ago,
                    exactDateTime: new Date(event.event_timestamp * 1000).toLocaleString(),
                    type: event.event_type,
                    buyer: event.from_wallet ? `${event.from_wallet.slice(0, 4)}...${event.from_wallet.slice(-4)}` : 'Unknown',
                    buyerFull: event.from_wallet || 'Unknown',
                    seller: event.to_wallet ? `${event.to_wallet.slice(0, 4)}...${event.to_wallet.slice(-4)}` : 'Unknown',
                    sellerFull: event.to_wallet || 'Unknown',
                    priceFormatted: event.sol_amount ? `${parseFloat(event.sol_amount).toFixed(2)} SOL` : 'N/A',
                    usdPriceFormatted: event.sol_amount ? `~$${getSOLUSDValue(parseFloat(event.sol_amount), solPrice)}` : 'N/A'
                  }));
                  console.log('✅ [ACTIVITY] Activity list refreshed!', formattedActivity.length, 'activities');
                  setSaleHistory(formattedActivity);
                }
              }
            } catch (error) {
              console.error('❌ [ACTIVITY] Error refreshing:', error);
            } finally {
              setIsLoadingActivity(false);
            }
          }, 500);
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log('✅ [ACTIVITY] Real-time subscription active');
        } else if (status === 'CHANNEL_ERROR') {
          console.error('❌ [ACTIVITY] Real-time subscription error');
        }
      });

    // Cleanup subscription on unmount
    return () => {
      console.log('📡 [ACTIVITY] Cleaning up real-time subscription');
      activitySubscription.unsubscribe();
    };
  }, [pieceId, solPrice]);

  // Load holders when tab is switched
  useEffect(() => {
    const loadHolders = async () => {
      if (activeTab !== 'holders' || !pieceId) return;
      
      setIsLoadingHolders(true);
      try {
        // If we have grouped piece IDs, fetch holders for all pieces in the group
        if (groupedPieceIds && groupedPieceIds.length > 0) {
          console.log('🔍 [HOLDERS] Fetching holders for grouped pieces:', groupedPieceIds);
          
          // Extract numeric IDs from strings like "supabase_123" or just "123"
          const numericIds = groupedPieceIds.map(id => {
            const numericId = typeof id === 'string' && id.includes('supabase_')
              ? parseInt(id.replace('supabase_', ''))
              : parseInt(id);
            return numericId;
          }).filter(id => !isNaN(id));
          
          console.log('🔢 [HOLDERS] Numeric piece IDs:', numericIds);
          
          // Fetch all pieces in the group with their holder info
          const { data: groupedPieces, error } = await supabase
            .from('pieces')
            .select(`
              id,
              holder_wallet,
              mint_address,
              listing_status,
              listing_price,
              name
            `)
            .in('id', numericIds);
          
          if (error) {
            console.error('❌ [HOLDERS] Error fetching grouped pieces:', error);
            setHolders([]);
            return;
          }
          
          console.log('📊 [HOLDERS] Found grouped pieces:', groupedPieces);
          
          if (!groupedPieces || groupedPieces.length === 0) {
            console.warn('⚠️ [HOLDERS] No pieces found for IDs:', groupedPieceIds);
            setHolders([]);
            return;
          }
          
          // Fetch creator profiles for all unique holder wallets
          const uniqueWallets = [...new Set(groupedPieces.map(p => p.holder_wallet).filter(Boolean))];
          console.log('👥 [HOLDERS] Unique holder wallets:', uniqueWallets);
          
          const { data: profiles } = await supabase
            .from('creators')
            .select('wallet, handle, profile_picture_url, is_verified, verification_type')
            .in('wallet', uniqueWallets);
          
          const profileMap = {};
          profiles?.forEach(p => {
            profileMap[p.wallet] = p;
          });
          
          // Sort pieces to match the order of groupedPieceIds
          // This ensures "Edition 1" in the holders list matches "Piece #1" in the selector
          const sortedPieces = numericIds.map(id => 
            groupedPieces.find(piece => piece.id === id)
          ).filter(Boolean);
          
          console.log('🔢 [HOLDERS] Sorted pieces to match input order:', sortedPieces.map(p => p.id));
          
          // Format holders data
          const formattedHolders = sortedPieces.map((piece, index) => ({
            editionNumber: index + 1,
            holderWallet: piece.holder_wallet,
            holderInfo: profileMap[piece.holder_wallet] || null,
            mintAddress: piece.mint_address,
            listingStatus: piece.listing_status,
            listingPrice: piece.listing_price
          }));
          
          console.log('✅ [HOLDERS] Formatted holders:', formattedHolders);
          setHolders(formattedHolders);
        } else {
          // Use the original edition holders logic for non-grouped pieces
          const editionHolders = await getEditionHolders(pieceId);
          setHolders(editionHolders);
        }
      } catch (error) {
        console.error('Error loading holders:', error);
        setHolders([]);
      } finally {
        setIsLoadingHolders(false);
      }
    };

    loadHolders();
  }, [activeTab, pieceId, groupedPieceIds]);

  const handleProfileClick = (walletAddress, holderInfo) => {
    // Use handle if available, otherwise use wallet address
    const profileIdentifier = holderInfo?.handle || walletAddress;
    console.log('🔍 Navigating to profile:', {
      walletAddress,
      handle: holderInfo?.handle,
      profileIdentifier,
      url: `/creator/${profileIdentifier}`
    });
    navigate(`/creator/${profileIdentifier}`);
  };

  const displayedSales = showAll ? saleHistory : saleHistory.slice(0, 3);

  return (
    <div>
      {/* Tab Headers - Minimal Design */}
      <div className="flex items-center justify-center gap-4 mb-3">
        <button
          onClick={() => setActiveTab('activity')}
          className={`text-sm font-medium transition-colors ${
            activeTab === 'activity'
              ? 'text-white'
              : 'text-gray-400 hover:text-gray-300'
          }`}
        >
          Activity
        </button>
        <button
          onClick={() => setActiveTab('holders')}
          className={`text-sm font-medium transition-colors ${
            activeTab === 'holders'
              ? 'text-white'
              : 'text-gray-400 hover:text-gray-300'
          }`}
        >
          Holders ({totalQuantity})
        </button>
      </div>

      {/* Tab Content */}
      <div>
        {/* Activity Tab */}
        {activeTab === 'activity' && (
          <div>
            {isLoadingActivity ? (
              <div className="space-y-2">
                {[...Array(2)].map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="p-3 bg-[#1a1a1a] rounded-lg">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 bg-gray-700 rounded"></div>
                          <div className="w-20 h-3 bg-gray-700 rounded"></div>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="text-center">
                            <div className="w-16 h-3 bg-gray-700 rounded mb-1"></div>
                            <div className="w-20 h-3 bg-gray-700 rounded"></div>
                          </div>
                          <div className="w-4 h-3 bg-gray-700 rounded"></div>
                          <div className="text-center">
                            <div className="w-16 h-3 bg-gray-700 rounded mb-1"></div>
                            <div className="w-20 h-3 bg-gray-700 rounded"></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : saleHistory.length === 0 ? (
              <div className="text-center py-8">
                <Activity size={32} className="mx-auto text-gray-600 mb-2" />
                <p className="text-gray-500 text-sm">No activity yet</p>
                <p className="text-gray-600 text-xs mt-1">
                  Activity will appear here once this piece is traded
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {displayedSales.map((sale) => (
                  <div key={sale.id} className="p-3 bg-[#1a1a1a] rounded-lg hover:bg-[#2a2a2a] transition-colors min-h-[80px] flex flex-col">
                    <div className="flex flex-col h-full">
                      {/* Event Type and Time */}
                      <div className="flex items-center gap-2 mb-2">
                        <div className={`w-2 h-2 rounded-full ${
                          sale.type.toLowerCase() === 'mint' ? 'bg-blue-500' :
                          sale.type.toLowerCase() === 'sale' ? 'bg-green-500' :
                          sale.type.toLowerCase() === 'transfer' ? 'bg-purple-500' :
                          sale.type.toLowerCase() === 'list' ? 'bg-yellow-500' :
                          'bg-gray-500'
                        }`}></div>
                        <span className="text-gray-400 text-xs capitalize">
                          {sale.type.toLowerCase() === 'list' ? 'listed' : sale.type.toLowerCase()}
                        </span>
                        <span className="text-gray-500 text-xs ml-auto" title={sale.exactDateTime}>
                          {sale.timeAgo}
                        </span>
                      </div>

                      {/* From and To - Fixed height section */}
                      <div className="flex-grow flex items-center justify-center min-h-[40px]">
                        {sale.type.toLowerCase() === 'mint' ? (
                          // Special display for mint events
                          <div className="flex items-center justify-center gap-2 text-xs">
                            <div className="text-gray-500">Minted to</div>
                            <button
                              onClick={() => handleProfileClick(sale.sellerFull)}
                              className="text-green-400 hover:text-green-300 transition-colors font-mono"
                            >
                              {sale.seller}
                            </button>
                          </div>
                        ) : sale.type.toLowerCase() === 'list' ? (
                          // Special display for list events
                          <div className="flex items-center justify-center gap-2 text-xs w-full">
                            <div className="text-gray-500">Listed by</div>
                            <button
                              onClick={() => handleProfileClick(sale.buyerFull || sale.sellerFull)}
                              className="text-yellow-400 hover:text-yellow-300 transition-colors font-mono"
                            >
                              {sale.buyer || sale.seller}
                            </button>
                            {/* Show listing price */}
                            {sale.priceFormatted !== 'N/A' && (
                              <>
                                <span className="text-gray-500">for</span>
                                <span className="text-green-400 font-semibold whitespace-nowrap">
                                  {sale.priceFormatted}
                                </span>
                              </>
                            )}
                          </div>
                        ) : (
                          // Standard display for transfers and sales
                          <div className="flex items-center justify-between text-xs w-full">
                            <div className="text-center">
                              <div className="text-gray-500 mb-0.5">From</div>
                              <button
                                onClick={() => handleProfileClick(sale.buyerFull)}
                                className="text-gray-300 hover:text-white transition-colors font-mono"
                              >
                                {sale.buyer}
                              </button>
                            </div>
                            {/* Show price for sales, arrow for transfers */}
                            {sale.type.toLowerCase() === 'sale' && sale.priceFormatted !== 'N/A' ? (
                              <div className="text-center px-2">
                                <div className="text-green-400 font-semibold whitespace-nowrap">
                                  {sale.priceFormatted}
                                </div>
                              </div>
                            ) : (
                              <div className="text-gray-600">→</div>
                            )}
                            <div className="text-center">
                              <div className="text-gray-500 mb-0.5">To</div>
                              <button
                                onClick={() => handleProfileClick(sale.sellerFull)}
                                className="text-gray-300 hover:text-white transition-colors font-mono"
                              >
                                {sale.seller}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
                
                {/* Show More/Less Button */}
                {saleHistory.length > 3 && (
                  <button
                    onClick={() => setShowAll(!showAll)}
                    className="w-full py-2 text-gray-400 hover:text-white text-sm transition-colors"
                  >
                    {showAll ? 'Show Less' : `Show All (${saleHistory.length})`}
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Holders Tab */}
        {activeTab === 'holders' && (
          <div>
            {isLoadingHolders ? (
              <div className="space-y-2">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="p-3 bg-[#1a1a1a] rounded-lg flex items-center gap-3">
                      <div className="w-10 h-10 bg-gray-700 rounded-full"></div>
                      <div className="flex-1">
                        <div className="w-24 h-3 bg-gray-700 rounded mb-1"></div>
                        <div className="w-16 h-3 bg-gray-700 rounded"></div>
                      </div>
                      <div className="w-16 h-3 bg-gray-700 rounded"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : holders.length === 0 ? (
              <div className="text-center py-8">
                <Users size={32} className="mx-auto text-gray-600 mb-2" />
                <p className="text-gray-500 text-sm">No holders yet</p>
                <p className="text-gray-600 text-xs mt-1">
                  Holders will appear here once editions are minted
                </p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-2">
                {holders.map((holder, index) => (
                  <button
                    key={holder.mintAddress || `holder-${index}-${holder.editionNumber || 'edition'}-${holder.holderWallet || 'unknown'}`}
                    onClick={() => handleProfileClick(holder.holderWallet, holder.holderInfo)}
                    className="w-full p-3 bg-[#1a1a1a] rounded-lg hover:bg-[#2a2a2a] transition-all cursor-pointer group"
                  >
                    <div className="flex items-center gap-3 overflow-hidden">
                      {/* Edition Number Badge */}
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-gradient-to-br from-gray-400/20 to-gray-500/20 border border-gray-400/30 rounded-lg flex items-center justify-center">
                          <span className="text-gray-300 text-xs font-bold">
                            #{holder.editionNumber || index + 1}
                          </span>
                        </div>
                      </div>

                      {/* Holder Avatar and Info */}
                      <div className="flex items-center gap-3 flex-1 min-w-0 text-left">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0">
                          {holder.holderInfo?.profile_picture_url ? (
                            <img
                              src={holder.holderInfo.profile_picture_url}
                              alt={holder.holderInfo.handle || 'Holder'}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center">
                              <span className="text-white font-bold text-sm">
                                {(holder.holderInfo?.handle || holder.holderWallet || 'A').charAt(0).toUpperCase()}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <span className="text-white text-sm font-medium truncate group-hover:text-green-400 transition-colors">
                              {holder.holderInfo?.handle || `${holder.holderWallet?.slice(0, 4)}...${holder.holderWallet?.slice(-4)}` || 'Anonymous'}
                            </span>
                            {holder.holderInfo?.is_verified && (
                              <VerifiedBadge 
                                size="sm"
                                verificationType={holder.holderInfo?.verification_type || 'none'}
                              />
                            )}
                          </div>
                          <div className="text-gray-500 text-xs group-hover:text-gray-400 transition-colors">
                            Edition {holder.editionNumber || index + 1} of {totalQuantity}
                          </div>
                        </div>
                      </div>

                      {/* Listing Status */}
                      <div className="flex-shrink-0 text-right">
                        {holder.listingStatus === 'listed' && holder.listingPrice ? (
                          <div>
                            <div className="text-green-400 text-xs font-semibold">
                              {holder.listingPrice} SOL
                            </div>
                            <div className="text-gray-500 text-xs">
                              Listed
                            </div>
                          </div>
                        ) : (
                          <div className="text-gray-500 text-xs">
                            Not listed
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PieceActivityAndHolders;

